/**
 * Journal form values and the Dutch 1–5 scales (spec §8.7).
 *
 * Status is always conveyed with icon + visible text, never color alone.
 */
import type { JournalEntry, NewJournalEntry } from '@/types'

export type JournalFormValues = {
  mood: number
  energy: number
  stress: number
  wentWell: string
  wasDifficult: string
  rememberTomorrow: string
  freewriting: string
  tags: string[]
}

export const EMPTY_JOURNAL_VALUES: JournalFormValues = {
  mood: 3,
  energy: 3,
  stress: 3,
  wentWell: '',
  wasDifficult: '',
  rememberTomorrow: '',
  freewriting: '',
  tags: [],
}

export type ScaleOption = {
  value: number
  /** Full accessible Dutch label, always visible. */
  label: string
}

/** Stemming: 'Zeer slecht' … 'Zeer goed'. */
export const MOOD_OPTIONS: readonly ScaleOption[] = [
  { value: 1, label: 'Zeer slecht' },
  { value: 2, label: 'Slecht' },
  { value: 3, label: 'Neutraal' },
  { value: 4, label: 'Goed' },
  { value: 5, label: 'Zeer goed' },
]

/** Energie: 'Zeer laag' … 'Zeer hoog'. */
export const ENERGY_OPTIONS: readonly ScaleOption[] = [
  { value: 1, label: 'Zeer laag' },
  { value: 2, label: 'Laag' },
  { value: 3, label: 'Gemiddeld' },
  { value: 4, label: 'Hoog' },
  { value: 5, label: 'Zeer hoog' },
]

/** Stress: 'Zeer laag' … 'Zeer hoog'. */
export const STRESS_OPTIONS: readonly ScaleOption[] = [
  { value: 1, label: 'Zeer laag' },
  { value: 2, label: 'Laag' },
  { value: 3, label: 'Gemiddeld' },
  { value: 4, label: 'Hoog' },
  { value: 5, label: 'Zeer hoog' },
]

export function scaleLabel(options: readonly ScaleOption[], value: number): string {
  return options.find((option) => option.value === value)?.label ?? `${value}`
}

export function valuesFromEntry(entry: JournalEntry): JournalFormValues {
  return {
    mood: entry.mood,
    energy: entry.energy,
    stress: entry.stress,
    wentWell: entry.wentWell,
    wasDifficult: entry.wasDifficult,
    rememberTomorrow: entry.rememberTomorrow,
    freewriting: entry.freewriting,
    tags: [...entry.tags],
  }
}

export function valuesToNewEntry(date: string, values: JournalFormValues): NewJournalEntry {
  return {
    date,
    mood: values.mood,
    energy: values.energy,
    stress: values.stress,
    wentWell: values.wentWell,
    wasDifficult: values.wasDifficult,
    rememberTomorrow: values.rememberTomorrow,
    freewriting: values.freewriting,
    tags: [...values.tags],
  }
}

export function valuesEqual(a: JournalFormValues, b: JournalFormValues): boolean {
  return (
    a.mood === b.mood &&
    a.energy === b.energy &&
    a.stress === b.stress &&
    a.wentWell === b.wentWell &&
    a.wasDifficult === b.wasDifficult &&
    a.rememberTomorrow === b.rememberTomorrow &&
    a.freewriting === b.freewriting &&
    a.tags.length === b.tags.length &&
    a.tags.every((tag, index) => tag === b.tags[index])
  )
}

/** True when nothing meaningful was entered (used to skip empty drafts). */
export function valuesAreEmpty(values: JournalFormValues): boolean {
  return valuesEqual(values, EMPTY_JOURNAL_VALUES)
}
